// Diff et fusion champ par champ d'un élément GeoJSON. Un chemin désigne un champ : 'geometry', 'properties.height',
// 'properties.customFields.12'. Fusion : le dernier gagne, champ par champ, sans conflits.

// Champs calculés par l'API : jamais considérés comme modifiés sur le terrain
export const SERVER_COMPUTED_PROPERTIES = [
    'fetchId', 'projectReference', 'creationDate', 'modificationDate', 'deletionDate',
    'carbonStock', 'coolingIndicator', 'coolingEnergyIndicator', 'amenityValue', 'biodiversityIndex'
];

// Copie profonde par JSON : rend rangeable une vue d'arbre binaire (Proxy), dates en chaînes ISO comme dans une réponse de l'API.
// À n'appeler que sur les arbres touchés, jamais dans une boucle sur tout le projet
export const toPlainFeature = (value) => (value === null || value === undefined ? value : JSON.parse(JSON.stringify(value)));

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

// Dates comparées à la seconde (chaîne sans fuseau lue comme de l'UTC, comme DatesUtil) ; décimaux en float32 comme les
// colonnes binaires, sinon une valeur lue dans un bloc paraîtrait modifiée et écraserait celle d'un collègue
const normalize = (value) => {
    if (typeof value === 'number' && !Number.isInteger(value)) return Math.fround(value);
    if (value instanceof Date) return { __date: Math.floor(value.getTime() / 1000) };
    if (typeof value === 'string' && ISO_DATE.test(value)) {
        const time = new Date(HAS_ZONE.test(value) ? value : value + 'Z').getTime();
        if (!isNaN(time)) return { __date: Math.floor(time / 1000) };
    }
    return value;
};

// JSON normalisé aux clés triées : deux valeurs égales donnent la même chaîne
export const stableStringify = (value) => {
    const normalized = normalize(value);
    if (normalized === undefined) return 'undefined';
    if (normalized === null || typeof normalized !== 'object') return JSON.stringify(normalized);
    if (Array.isArray(normalized)) return '[' + normalized.map(stableStringify).join(',') + ']';
    return '{' + Object.keys(normalized).sort()
        .filter(key => normalized[key] !== undefined)
        .map(key => JSON.stringify(key) + ':' + stableStringify(normalized[key]))
        .join(',') + '}';
};

export const isSameValue = (a, b) => stableStringify(a) === stableStringify(b);

export const getPath = (feature, path) => path.split('.').reduce((object, key) => (object === null || object === undefined ? undefined : object[key]), feature);

const setPath = (feature, path, value) => {
    const keys = path.split('.');
    let object = feature;
    keys.slice(0, -1).forEach(key => {
        if (object[key] === null || object[key] === undefined || typeof object[key] !== 'object') object[key] = {};
        object = object[key];
    });
    const last = keys[keys.length - 1];
    if (value === undefined) delete object[last];
    else object[last] = value;
};

// trunks, observations et dimensions comparés en entier ; champs personnalisés un par un
const listPaths = (feature) => {
    const paths = ['geometry'];
    const properties = feature?.properties || {};
    Object.keys(properties).forEach(key => {
        if (SERVER_COMPUTED_PROPERTIES.includes(key)) return;
        if (key === 'customFields' && properties.customFields && typeof properties.customFields === 'object')
            Object.keys(properties.customFields).forEach(id => paths.push(`properties.customFields.${id}`));
        else paths.push(`properties.${key}`);
    });
    return paths;
};

// changes : nouvelles valeurs des champs modifiés ; base : leurs valeurs d'origine (compactage, annulation locale)
export const diffFeatures = (before, after) => {
    const changes = {}, base = {};
    new Set([...listPaths(before), ...listPaths(after)]).forEach(path => {
        const oldValue = getPath(before, path), newValue = getPath(after, path);
        // Coordonnées en float64 partout : comparées sans arrondi (le pas du float32 dépasse 40 cm en latitude)
        const isSame = path === 'geometry'
            ? JSON.stringify([oldValue?.type, oldValue?.coordinates]) === JSON.stringify([newValue?.type, newValue?.coordinates])
            : isSameValue(oldValue, newValue);
        if (!isSame) {
            changes[path] = toPlainFeature(newValue);
            base[path] = toPlainFeature(oldValue);
        }
    });
    return { changes, base };
};

export const applyChanges = (feature, changes) => {
    const result = toPlainFeature(feature);
    Object.entries(changes || {}).forEach(([path, value]) => setPath(result, path, toPlainFeature(value)));
    return result;
};

// Champs du terrain appliqués à la version serveur relue avant l'envoi ; les autres gardent la valeur serveur
export const mergeFeature = (serverFeature, changes) => ({ merged: applyChanges(serverFeature, changes) });
